import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import PageNavigation from "@/components/PageNavigation";
import SEOHead from "@/components/seo/SEOHead";
import juniorBianchim from "@/assets/junior-bianchim.jpg";
import postCoverEstrategias from "@/assets/cover-13-estrategias.jpg";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import {
  ArrowRight,
  Clock,
  User,
  BookOpen,
  Target,
  Lightbulb,
  Zap,
  Trophy,
  Shield,
  CheckCircle,
  ChevronRight,
  TrendingUp,
  Brain,
  BarChart3,
  Layers,
  Star,
  Award,
  Crosshair,
  Gauge,
  Search,
  Users,
  DollarSign,
  Timer,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const EstrategiasVendas2026 = () => {
  const navigate = useNavigate();

  const strategies = [
    {
      number: "01",
      name: "SPIN Selling",
      author: "Neil Rackham",
      structure: ["Situation", "Problem", "Implication", "Need-payoff"],
      objective: "Venda consultiva baseada em perguntas estruturadas.",
      whenToUse: ["Vendas complexas", "Cliente ainda não percebeu o tamanho do problema"],
      level: "Diagnóstico estratégico",
      levelNum: 3,
      icon: Search,
      color: "text-primary",
      description: `O SPIN Selling é, sem dúvida, um dos frameworks mais influentes da história das vendas. Criado por Neil Rackham após analisar mais de 35.000 ligações de vendas ao longo de 12 anos, o método provou cientificamente que vendedores de alto desempenho fazem perguntas de forma diferente.

A lógica é elegante: em vez de despejar features e benefícios, o vendedor guia o prospect através de quatro fases de perguntas. Primeiro, entende a **Situação** (contexto atual). Depois, explora **Problemas** que o prospect enfrenta. Em seguida, aprofunda as **Implicações** desses problemas — é aqui que a mágica acontece, pois o prospect percebe o real custo de não agir. Por fim, faz perguntas de **Need-payoff** que levam o próprio prospect a verbalizar os benefícios da solução.

Na prática, imagine que você vende software de CRM para empresas. Em vez de dizer "nosso CRM aumenta vendas em 30%", você perguntaria: "Como sua equipe acompanha o pipeline hoje? Quantas oportunidades são perdidas por falta de follow-up? Se cada vendedor perder 3 deals por mês por falta de acompanhamento, quanto isso representa em receita anual? Se existisse uma forma de garantir que nenhum follow-up fosse esquecido, como isso impactaria suas metas?"

O poder do SPIN está em fazer o prospect convencer a si mesmo. Quando ele próprio articula o problema e o impacto, a resistência à compra cai dramaticamente. Estudos mostram que vendedores treinados em SPIN têm taxas de fechamento 17% superiores em vendas complexas.

**Dica avançada**: Não use SPIN de forma mecânica. As melhores aplicações do framework são naturais, como uma conversa genuína de descoberta. Prepare suas perguntas antes da reunião, mas esteja pronto para improvisar baseado nas respostas.`,
    },
    {
      number: "02",
      name: "BANT",
      author: "IBM",
      structure: ["Budget", "Authority", "Need", "Timing"],
      objective: "Qualificação rápida de lead.",
      whenToUse: ["Pré-venda (SDR)", "Pipeline volumoso"],
      level: "Tático",
      levelNum: 1,
      icon: Target,
      color: "text-accent",
      description: `O BANT é o avô dos frameworks de qualificação. Criado pela IBM nas décadas de 60-70, ele continua sendo usado por milhares de equipes ao redor do mundo — e por bons motivos. Sua simplicidade é sua força.

A ideia é direta: antes de investir tempo e energia em um prospect, valide quatro critérios. O prospect tem **Budget** (orçamento) para comprar? Você está falando com quem tem **Authority** (autoridade) para decidir? Existe um **Need** (necessidade) real e reconhecida? E o **Timing** (prazo) é favorável?

Em 2026, o BANT ainda funciona extraordinariamente bem em cenários de alto volume, onde SDRs precisam qualificar dezenas de leads por dia. É como um filtro rápido: se o lead não passa nos quatro critérios, não vale aprofundar. Isso protege o tempo dos vendedores seniores e mantém o pipeline limpo.

No entanto, há críticas válidas. Em vendas modernas, especialmente SaaS, o orçamento muitas vezes é flexível — pode ser criado se a dor for grande o suficiente. Além disso, começar perguntando sobre dinheiro pode parecer agressivo. Por isso, muitas equipes reordenam os critérios: começam por Need, depois Authority, Timing, e por último Budget.

**Como aplicar em 2026**: Use BANT como checklist mental, não como script. Em uma conversa de 5 minutos, tente cobrir os quatro pontos naturalmente. Se descobrir que faltam 2 ou mais critérios, coloque o lead em nurturing em vez de descartá-lo completamente. 

**Armadilha comum**: Tratar BANT como uma lista rígida de sim/não. Em mercados dinâmicos, um "não" em Budget pode virar "sim" no próximo trimestre. Registre tudo no CRM e crie cadências de reativação.`,
    },
    {
      number: "03",
      name: "PUT(O)",
      author: "Modelo de campo",
      structure: ["Problema", "Urgência", "Tendência", "Orçamento"],
      objective: "Entender dor real + pressão temporal.",
      whenToUse: ["Vendas consultivas", "Quando o timing é crítico"],
      level: "Estratégico",
      levelNum: 2,
      icon: Timer,
      color: "text-secondary",
      description: `O PUT(O) é um framework menos acadêmico, mais de trincheira. Nasceu da prática comercial brasileira e latino-americana, onde vendedores precisam de uma forma rápida e eficaz de mapear o cenário do prospect sem parecer robótico.

A estrutura é intuitiva: **Problema** — qual é a dor real, não a superficial? **Urgência** — o prospect precisa resolver isso quando? **Tendência** — o problema está piorando, estável ou melhorando? E **Orçamento** — existe recurso financeiro disponível?

O diferencial do PUT(O) em relação ao BANT é a inclusão de "Tendência". Esse elemento é poderoso porque cria senso de urgência natural. Quando o vendedor ajuda o prospect a perceber que o problema está piorando — crescendo mês a mês, afetando mais áreas, custando mais — a decisão de compra se torna mais urgente sem que o vendedor precise pressionar artificialmente.

Na prática, funciona assim: "Entendi que a rotatividade da equipe comercial é um problema. Isso está acontecendo há quanto tempo? Nos últimos meses, piorou, manteve ou melhorou? Se continuar nessa tendência por mais 6 meses, qual seria o impacto?"

Perceba como a pergunta sobre tendência funciona como as perguntas de Implicação do SPIN, mas de forma mais direta e ágil. É ideal para vendedores que não têm 45 minutos para uma reunião de discovery completa.

**Quando brilha**: Em vendas com ciclo médio (15-45 dias), onde o prospect já reconhece que tem um problema mas está procrastinando a decisão. O PUT(O) ajuda a criar a pressão temporal que falta para fechar.

**Dica**: Documente a Tendência no CRM. Ela é o melhor argumento para follow-ups futuros: "Na última vez que conversamos, o problema estava em X. Como está agora?"`,
    },
    {
      number: "04",
      name: "KO (Knockout Criteria)",
      author: "Conceito universal",
      structure: ["Critérios eliminatórios"],
      objective: "Evitar desperdício de tempo.",
      whenToUse: ["Antes de aprofundar a venda"],
      level: "Tático",
      levelNum: 1,
      icon: Shield,
      color: "text-destructive",
      description: `O KO — ou Knockout Criteria — é menos um framework de vendas e mais uma filosofia de eficiência comercial. A ideia é brutal na sua simplicidade: defina critérios eliminatórios e, se o lead falhar em qualquer um deles, saia rápido.

Exemplos de critérios KO comuns: Não tem orçamento mínimo de R$ X? KO. Não é o decisor e não consegue envolver quem decide? KO. Não tem um problema claro que seu produto resolve? KO. O prazo de decisão é superior a 6 meses? KO.

A beleza do KO está em proteger o recurso mais precioso de qualquer vendedor: tempo. Vendedores medianos desperdiçam 60% do tempo com prospects que nunca vão fechar. Os melhores vendedores são implacáveis em desqualificar rapidamente para investir nos deals que realmente importam.

Em 2026, com o aumento exponencial de leads gerados por IA e automação, ter critérios KO claros é mais importante do que nunca. Sem filtros, o vendedor se afoga em volume sem qualidade.

**Como implementar**: Reúna seu time e defina 3-5 critérios KO inegociáveis. Treine SDRs para identificá-los nos primeiros 3 minutos de conversa. Se o lead falhar em qualquer um, redirecione para nurturing automatizado em vez de continuar gastando tempo humano.

**Atenção**: KO não significa ser rude. Você pode desqualificar com elegância: "Parece que o momento não é ideal para esse tipo de solução. Vou te adicionar em nossa newsletter com conteúdos que podem ser úteis, e revisitamos no próximo trimestre."

**Armadilha**: Critérios KO muito restritivos matam o pipeline. Muito amplos, não filtram nada. Revise trimestralmente baseado em dados de fechamento.`,
    },
    {
      number: "05",
      name: "PICC",
      author: "Versão simplificada de MEDDIC",
      structure: ["Pain", "Impact", "Champion", "Competition"],
      objective: "Venda enterprise com foco em influência interna.",
      whenToUse: ["Vendas enterprise", "Ciclos longos com múltiplos decisores"],
      level: "Enterprise",
      levelNum: 4,
      icon: Users,
      color: "text-primary",
      description: `O PICC é uma versão enxuta e acessível do poderoso MEDDIC, mantendo os elementos mais críticos sem a complexidade que assusta muitos vendedores. É ideal para equipes que vendem para empresas de médio e grande porte e precisam de estrutura sem burocracia.

**Pain (Dor)**: Qual é a dor específica e quantificável do prospect? Não aceite respostas vagas como "queremos melhorar". Aprofunde até chegar em números: "Quanto isso custa por mês?" ou "Quantas horas sua equipe perde com isso semanalmente?"

**Impact (Impacto)**: Se a dor for resolvida, qual o impacto positivo mensurável? Aumento de receita? Redução de custos? Ganho de produtividade? O impacto é o que justifica o investimento para o economic buyer.

**Champion**: Quem dentro da organização do prospect é o seu defensor interno? Essa pessoa acredita na sua solução e vai vender por você nas reuniões em que você não está presente. Sem um Champion forte, deals enterprise morrem silenciosamente.

**Competition (Concorrência)**: O que mais está sendo avaliado? Não apenas concorrentes diretos, mas o status quo (não fazer nada) e soluções internas (planilhas, processos manuais). Entender a competição permite posicionar melhor sua solução.

O PICC brilha porque aborda o aspecto político das vendas enterprise que frameworks mais simples ignoram. Em uma empresa com 500+ funcionários, a decisão de compra raramente é puramente racional — há territórios, egos, agendas e dinâmicas de poder em jogo.

**Dica avançada**: Invista 70% do seu esforço em encontrar e nutrir o Champion certo. O Champion ideal não é necessariamente quem tem mais poder, mas quem tem mais a ganhar com sua solução e coragem para defendê-la internamente.`,
    },
    {
      number: "06",
      name: "GPCTBA C&I",
      author: "HubSpot",
      structure: ["Goals", "Plans", "Challenges", "Timeline", "Budget", "Authority", "Consequences & Implications"],
      objective: "Mapear estratégia do cliente e risco da inação.",
      whenToUse: ["Vendas consultivas de alta maturidade", "Quando o prospect tem estratégia clara"],
      level: "Alta maturidade consultiva",
      levelNum: 3,
      icon: Brain,
      color: "text-accent",
      description: `O GPCTBA C&I é o framework mais abrangente dessa lista. Criado pela HubSpot, ele vai muito além da qualificação superficial e mergulha na estratégia completa do prospect. Não é para iniciantes — exige maturidade consultiva e capacidade de conduzir conversas estratégicas de alto nível.

**Goals**: Quais são os objetivos de negócio do prospect para os próximos 6-12 meses? Crescer 30%? Expandir para novos mercados? Reduzir churn? Entender os goals permite alinhar sua solução com o que realmente importa para o C-level.

**Plans**: O que já está planejado para atingir esses objetivos? Quais iniciativas estão em andamento? Isso revela onde sua solução se encaixa — ou não — na estratégia já existente.

**Challenges**: Quais obstáculos estão impedindo o progresso? Aqui está sua oportunidade de posicionar sua solução como a peça que falta no quebra-cabeça.

**Timeline**: Quando esses objetivos precisam ser alcançados? Isso determina a urgência real da compra.

**Budget e Authority**: Semelhantes ao BANT, mas contextualizados dentro da estratégia maior, o que torna as respostas mais ricas.

**Consequences & Implications**: Esta é a parte mais poderosa. O que acontece se o prospect NÃO agir? Quais são as consequências de manter o status quo? E quais são as implicações positivas de resolver o problema?

Em 2026, o GPCTBA C&I é particularmente relevante porque compradores B2B estão mais sofisticados. Eles não querem um vendedor que entende de produto — querem um consultor que entende de negócio. Dominar este framework posiciona você como parceiro estratégico.

**Quando NÃO usar**: Em vendas rápidas, de baixo ticket ou com decisores operacionais. Ele é overkill para deals simples e pode frustrar prospects que querem agilidade.`,
    },
    {
      number: "07",
      name: "FANT",
      author: "Derivado do BANT",
      structure: ["Funds", "Authority", "Need", "Timing"],
      objective: "Qualificação rápida simplificada.",
      whenToUse: ["Vendas rápidas", "Menor complexidade"],
      level: "Tático",
      levelNum: 1,
      icon: Zap,
      color: "text-accent",
      description: `O FANT é essencialmente uma variação prática do BANT que substitui "Budget" por "Funds" — uma mudança sutil mas significativa. Enquanto "Budget" implica um orçamento formal e aprovado, "Funds" pergunta se existem recursos disponíveis, mesmo que não estejam formalmente alocados.

Essa distinção importa especialmente em startups, PMEs e empresas com orçamentos flexíveis, onde não existe um "budget de TI" formal, mas há dinheiro disponível para resolver problemas urgentes.

O FANT funciona excepcionalmente bem em ciclos de venda curtos (7-20 dias), onde o vendedor precisa qualificar rapidamente sem a profundidade de frameworks como SPIN ou MEDDIC. É o canivete suíço do vendedor que trabalha com volume.

**Aplicação prática**: Em uma ligação de qualificação de 5-7 minutos, cubra os quatro pontos naturalmente. "Vocês já têm algum recurso separado para resolver esse tipo de problema?" (Funds). "Quem mais participaria dessa decisão?" (Authority). "O que motivou vocês a procurarem uma solução agora?" (Need). "Quando vocês gostariam de ter isso implementado?" (Timing).

**Vantagem sobre o BANT**: A pergunta sobre Funds é menos intimidadora e gera respostas mais honestas. Prospects se sentem menos pressionados e mais abertos a compartilhar informações sobre disponibilidade de recursos.

**Melhor cenário de uso**: Inside sales com alto volume de leads, onde cada ligação de qualificação precisa ser eficiente e objetiva. Se você faz 40+ ligações por dia, FANT é seu melhor amigo.`,
    },
    {
      number: "08",
      name: "MEDDIC",
      author: "PTC (Parametric Technology Corporation)",
      structure: ["Metrics", "Economic Buyer", "Decision Criteria", "Decision Process", "Identify Pain", "Champion"],
      objective: "Previsibilidade de fechamento em contratos grandes.",
      whenToUse: ["Vendas enterprise", "Contratos de alto valor", "Ciclos longos"],
      level: "Altamente estratégico",
      levelNum: 4,
      icon: Trophy,
      color: "text-primary",
      description: `O MEDDIC é o framework de vendas enterprise mais respeitado do mundo. Criado na PTC nos anos 90, ele transformou uma equipe de vendas desorganizada em uma máquina previsível que gerou bilhões em receita. Hoje, é adotado por gigantes como Salesforce, MongoDB, e centenas de scale-ups que vendem soluções complexas.

**Metrics**: Quais métricas de negócio o prospect espera impactar? Não aceite respostas qualitativas. Você precisa de números: "Queremos aumentar conversão de 5% para 8%" ou "Precisamos reduzir tempo de onboarding de 30 para 10 dias". Métricas são o que você vai usar para construir o business case.

**Economic Buyer**: Quem assina o cheque? Não o influenciador, não o campeão — o decisor econômico final. Em deals enterprise, ignorar esse ponto é o erro #1 que mata negociações na reta final.

**Decision Criteria**: Quais critérios técnicos, comerciais e políticos serão usados para escolher o fornecedor? Se você não sabe os critérios, não pode moldá-los a seu favor.

**Decision Process**: Quais são as etapas formais até a assinatura do contrato? Precisa de POC? Comitê de aprovação? Procurement? Jurídico? Cada etapa não mapeada é uma emboscada potencial.

**Identify Pain**: Qual é a dor do Economic Buyer — não do usuário final, mas de quem paga? A dor do C-level geralmente é diferente da dor operacional. É preciso conectar as duas.

**Champion**: Semelhante ao PICC, mas com critérios mais rigorosos. No MEDDIC, o Champion precisa ter três qualidades: poder de influência, acesso ao Economic Buyer, e interesse pessoal no sucesso da sua solução.

O MEDDIC não é para vendedores casuais. Exige disciplina, processo e investimento significativo de tempo em cada deal. Mas para quem vende contratos de R$ 100k+, é a diferença entre forecast confiável e achismo.

**Em 2026**: O MEDDIC ganha uma camada extra com ferramentas de IA que ajudam a mapear automaticamente stakeholders, critérios de decisão e processos de compra baseados em dados históricos.`,
    },
    {
      number: "09",
      name: "ANUM",
      author: "Evolução do BANT",
      structure: ["Authority", "Need", "Urgency", "Money"],
      objective: "Priorizar autoridade no processo de qualificação.",
      whenToUse: ["Quando decisores são difíceis de acessar", "Vendas B2B com hierarquia complexa"],
      level: "Estratégico",
      levelNum: 2,
      icon: Award,
      color: "text-secondary",
      description: `O ANUM é a resposta à principal crítica do BANT: por que começar por orçamento quando o mais importante é saber se você está falando com a pessoa certa?

A reordenação é estratégica. **Authority** vem primeiro porque não adianta ter a melhor conversa do mundo com alguém que não pode decidir. Em vendas B2B, estima-se que 60% do tempo dos vendedores é desperdiçado com interlocutores sem poder de decisão.

**Need** vem em segundo porque, mesmo falando com o decisor, se não há necessidade real, não há venda. Aqui, explore a dor com profundidade: não aceite "estamos bem" como resposta. Faça perguntas que revelem necessidades latentes.

**Urgency** é o diferencial do ANUM. Enquanto o BANT usa "Timing" (quando), o ANUM foca em "Urgência" (quão urgente). A diferença é sutil mas poderosa: timing é passivo ("planejamos para Q3"), urgência é ativa ("precisamos resolver agora porque...").

**Money** fica por último deliberadamente. Quando você já estabeleceu autoridade, necessidade e urgência, a conversa sobre dinheiro acontece naturalmente e com menos resistência. O prospect já está convencido de que precisa resolver o problema — agora é só questão de quanto está disposto a investir.

**Aplicação prática**: Na primeira ligação, antes de qualquer coisa, descubra quem decide. "Além de você, quem mais participa desse tipo de decisão?". Se a pessoa não tem autoridade, seu próximo passo é pedir uma introdução ao decisor, não continuar vendendo para quem não pode comprar.

**Dica de campo**: Decisores raramente se identificam como tal na primeira conversa. Pergunte: "Se você decidisse seguir com uma solução como a nossa, quais seriam os próximos passos internos?" A resposta revela a cadeia de decisão.`,
    },
    {
      number: "10",
      name: "CAMP",
      author: "Oracle",
      structure: ["Challenges", "Authority", "Money", "Prioritization"],
      objective: "Entender prioridade estratégica antes de orçamento.",
      whenToUse: ["Vendas enterprise", "Prospect com múltiplas iniciativas concorrendo por orçamento"],
      level: "Estratégico",
      levelNum: 2,
      icon: Layers,
      color: "text-primary",
      description: `O CAMP foi criado pela Oracle, uma empresa que entende como poucos a dinâmica de vendas enterprise onde projetos competem ferozmente por orçamento e atenção executiva.

O diferencial do CAMP está no P — **Prioritization**. Em grandes organizações, o problema não é falta de dinheiro, mas excesso de projetos concorrendo por recursos limitados. Seu prospect pode reconhecer que precisa da sua solução e ter orçamento, mas se ela não for prioridade, vai para o final da fila.

**Challenges**: Comece entendendo os desafios de negócio, não os técnicos. "Quais são os três maiores desafios que o board cobra de vocês este ano?" Isso posiciona a conversa em nível estratégico desde o início.

**Authority**: Mapeie a estrutura de poder. Quem influencia, quem decide, quem bloqueia. Em organizações complexas como as que a Oracle atende, pode haver 10+ pessoas envolvidas em uma decisão.

**Money**: O dinheiro existe, mas está alocado para quê? Entender a distribuição orçamentária revela onde sua solução precisa competir por recursos.

**Prioritization**: Esta é a pergunta de ouro: "De 1 a 10, onde esse projeto se posiciona na lista de prioridades da empresa?" Se a resposta for menor que 7, você tem trabalho a fazer — ou precisa aceitar que o timing não é agora.

**Tática avançada**: Use eventos trigger para elevar a prioridade. Se o concorrente do prospect acaba de implementar uma solução semelhante, isso pode mover seu projeto de prioridade 5 para prioridade 9 overnight. Monitore o mercado e use inteligência competitiva a seu favor.

**Em 2026**: Com ferramentas de IA mapeando prioridades corporativas via relatórios públicos, calls de earnings e posts executivos no LinkedIn, o CAMP se torna ainda mais poderoso.`,
    },
    {
      number: "11",
      name: "CHAMP",
      author: "Evolução moderna do BANT",
      structure: ["Challenges", "Authority", "Money", "Prioritization"],
      objective: "Começar pela dor estratégica do prospect.",
      whenToUse: ["Vendas SaaS", "Inside sales B2B"],
      level: "Estratégico",
      levelNum: 2,
      icon: Crosshair,
      color: "text-accent",
      description: `O CHAMP é frequentemente confundido com o CAMP, e com razão — as siglas são quase idênticas. A diferença está na filosofia: enquanto o CAMP nasceu na Oracle focado em enterprise, o CHAMP foi popularizado pelo ecossistema SaaS e é mais ágil e orientado à ação.

A letra C no CHAMP representa **Challenges** (Desafios), não "Company" ou "Context". A premissa é que toda venda começa com um desafio que o prospect não consegue resolver sozinho. Se não há desafio, não há venda — simples assim.

**Challenges**: "Qual o maior desafio que sua equipe enfrenta hoje em [área específica]?". Essa abertura é poderosa porque convida o prospect a compartilhar vulnerabilidades. Quando ele abre esse espaço, a confiança cresce exponencialmente.

**Authority**: No contexto SaaS, autoridade é mais fluida. Um gerente pode ter autonomia para aprovar ferramentas até R$ 5k/mês sem envolver o C-level. Descubra os limites de autoridade e adapte sua estratégia.

**Money**: Em SaaS, a conversa de dinheiro gira ao redor de ROI e payback. "Quanto esse desafio custa por mês? Nossa solução custa X. Isso significa payback em Y meses." Se o ROI é claro, o dinheiro raramente é bloqueio real.

**Prioritization**: Similar ao CAMP, mas com uma nuance SaaS: o prospect pode estar avaliando 5 ferramentas diferentes ao mesmo tempo. Sua prioridade não é apenas vs. outros projetos, mas vs. outras ferramentas que competem pela atenção e cartão corporativo.

**Por que é popular em SaaS**: Ciclos mais curtos, decisores mais acessíveis, e a abordagem "desafio-primeiro" ressoa com uma geração de compradores que valoriza empatia sobre pressão.

**Dica**: Use o CHAMP para criar urgência sem agressividade. "Entendi que esse desafio custa R$ 20k/mês. A cada mês que passa sem resolver, são mais R$ 20k. Faz sentido priorizar isso agora?"`,
    },
    {
      number: "12",
      name: "NEAT",
      author: "The Harris Consulting Group",
      structure: ["Need", "Economic Impact", "Access to Authority", "Timeline"],
      objective: "Focar no impacto financeiro da dor.",
      whenToUse: ["Vendas com forte componente de ROI", "Quando o prospect precisa justificar internamente"],
      level: "Estratégico",
      levelNum: 2,
      icon: DollarSign,
      color: "text-primary",
      description: `O NEAT foi criado pelo The Harris Consulting Group com uma premissa clara: em vendas modernas, o impacto econômico da dor é o que move decisões. Não basta saber que o prospect tem um problema — é preciso quantificar o custo de não resolvê-lo.

**Need**: Diferente de outros frameworks, o NEAT não pergunta "qual sua necessidade?" de forma genérica. Ele busca a necessidade central — aquela que, se resolvida, transformaria o negócio. É a necessidade que tira o sono do CEO, não a funcionalidade que seria "legal ter".

**Economic Impact**: Esta é a estrela do NEAT. Quanto o problema custa? Quanto a solução vai gerar? O NEAT exige que o vendedor saiba fazer contas de negócio. "Sua equipe perde 10 horas por semana com processos manuais. Com um custo médio de R$ 80/hora, isso é R$ 3.200/mês ou R$ 38.400/ano. Nossa solução custa R$ 15.000/ano. O ROI é de 156%."

Quando o vendedor domina a linguagem financeira, ele deixa de ser percebido como vendedor e passa a ser visto como consultor de negócios. Essa mudança de percepção é o que separa vendedores de R$ 5k/mês de vendedores de R$ 50k/mês.

**Access to Authority**: Note que não é "Authority" — é "Access to Authority". O NEAT reconhece que, na maioria das vezes, o vendedor não está falando diretamente com o decisor. O framework foca em como acessar essa pessoa: através do Champion, de uma introdução formal, de um evento executive.

**Timeline**: Quando a decisão precisa ser tomada? Não quando o prospect "gostaria", mas quando precisa. Prazos regulatórios, deadlines de board, fim de ano fiscal — esses são timelines reais que criam urgência genuína.

**Em 2026**: Com o acesso a dados financeiros via IA (ferramentas que analisam 10-Ks, relatórios anuais, calls de earnings), vendedores podem construir business cases muito mais robustos antes mesmo da primeira reunião.`,
    },
    {
      number: "13",
      name: "DECIDE",
      author: "Framework enterprise",
      structure: ["Decision Criteria", "Economic Buyer", "Champion", "Identify Pain", "Decision Process", "Evaluate Competition"],
      objective: "Máxima previsibilidade em vendas complexas.",
      whenToUse: ["Contratos de alto valor", "Processo de decisão com múltiplas etapas"],
      level: "Enterprise",
      levelNum: 4,
      icon: BarChart3,
      color: "text-accent",
      description: `O DECIDE é o framework para quem quer o máximo de previsibilidade em vendas enterprise complexas. Muito parecido com o MEDDIC em essência, mas com uma ênfase ainda maior no processo decisório do comprador.

**Decision Criteria**: Quais critérios formais e informais serão usados para escolher o fornecedor? Técnicos (funcionalidades, integrações, segurança), comerciais (preço, condições, SLA) e políticos (relacionamento existente, preferência interna, risco percebido). Se você não conhece todos os critérios, está voando às cegas.

**Economic Buyer**: Quem é a pessoa que pode dizer "sim" sem precisar de mais aprovações? Em enterprises, essa pessoa geralmente é VP ou C-level. Você precisa de acesso direto ou indireto (via Champion) a essa pessoa.

**Champion**: Seu aliado interno. No DECIDE, o Champion tem um papel ainda mais ativo: ele não apenas defende sua solução, mas também informa sobre movimentos internos, mudanças de critério e ações da concorrência.

**Identify Pain**: A dor precisa ser multinível: dor do usuário final (operacional), dor do gerente (eficiência), e dor do executivo (estratégica/financeira). Conectar as três cria um caso irrefutável.

**Decision Process**: Mapeie cada etapa desde o "estamos interessados" até a assinatura do contrato. RFP? POC? Comitê de avaliação? Procurement? Jurídico? Board approval? Cada etapa precisa de uma estratégia específica.

**Evaluate Competition**: Não ignore a concorrência. Saiba quem mais está na mesa, quais são seus pontos fortes e fracos, e como se posicionar. A melhor estratégia não é atacar o concorrente, mas reframing: mude os critérios de avaliação para aqueles onde você é imbatível.

O DECIDE é pesado, complexo e demanda disciplina extrema de preenchimento no CRM. Mas para deals de R$ 500k+, é a diferença entre previsibilidade e loteria.

**Dica final**: O DECIDE funciona como um mapa do tesouro. Cada elemento preenchido remove uma camada de incerteza. Quando todos os seis estão mapeados com alta confiança, sua taxa de fechamento ultrapassa 80%.`,
    },
  ];

  const comparisonTable = [
    { model: "BANT", focus: "Qualificação básica", complexity: "Baixa" },
    { model: "FANT", focus: "Qualificação rápida", complexity: "Baixa" },
    { model: "KO", focus: "Desqualificação", complexity: "Baixa" },
    { model: "ANUM", focus: "Autoridade primeiro", complexity: "Média" },
    { model: "CHAMP", focus: "Dor estratégica", complexity: "Média" },
    { model: "CAMP", focus: "Prioridade estratégica", complexity: "Média" },
    { model: "NEAT", focus: "Impacto financeiro", complexity: "Média" },
    { model: "PUT(O)", focus: "Dor + urgência", complexity: "Média" },
    { model: "SPIN", focus: "Diagnóstico profundo", complexity: "Alta" },
    { model: "GPCTBA C&I", focus: "Estratégia completa", complexity: "Alta" },
    { model: "PICC", focus: "Influência interna", complexity: "Alta" },
    { model: "MEDDIC", focus: "Enterprise", complexity: "Muito Alta" },
    { model: "DECIDE", focus: "Processo decisório", complexity: "Muito Alta" },
  ];

  const maturityLevels = [
    {
      level: "Nível 1 — Qualificação Básica",
      models: ["BANT", "FANT", "KO"],
      description: "Ideal para SDRs e equipes com alto volume de leads. Filtram rápido e mantêm o pipeline limpo.",
      color: "bg-accent/20 border-accent/40",
      icon: Gauge,
    },
    {
      level: "Nível 2 — Qualificação Estratégica",
      models: ["ANUM", "CHAMP", "CAMP", "NEAT", "PUT(O)"],
      description: "Para vendedores intermediários que precisam aprofundar a qualificação sem a complexidade enterprise.",
      color: "bg-primary/20 border-primary/40",
      icon: Target,
    },
    {
      level: "Nível 3 — Venda Consultiva Estruturada",
      models: ["SPIN", "GPCTBA C&I"],
      description: "Exige maturidade consultiva. Ideal para vendas complexas com ciclos de 30-90 dias.",
      color: "bg-secondary/20 border-secondary/40",
      icon: Brain,
    },
    {
      level: "Nível 4 — Enterprise Previsível",
      models: ["MEDDIC", "DECIDE", "PICC"],
      description: "Frameworks para contratos de alto valor, múltiplos decisores e máxima previsibilidade de forecast.",
      color: "bg-primary/20 border-primary/40",
      icon: Trophy,
    },
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: "13 Estratégias de Vendas Para Dominar Qualquer Negociação em 2026",
    description: "Guia definitivo com 13 frameworks de vendas — de BANT a MEDDIC — com tabela comparativa, níveis de maturidade e aplicação prática para cada cenário comercial.",
    author: { "@type": "Person", name: "Junior Bianchim" },
    publisher: { "@type": "Organization", name: "Bianchim Business" },
    datePublished: "2026-01-13",
    dateModified: "2026-01-13",
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": "https://bianchimbusiness.com.br/posts/13-estrategias-vendas-2026",
    },
  };

  const faqData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "Qual a melhor estratégia de vendas para 2026?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Depende do seu contexto. Para vendas rápidas, BANT ou FANT. Para vendas consultivas, SPIN ou GPCTBA. Para enterprise, MEDDIC ou DECIDE. O ideal é dominar 2-3 frameworks complementares.",
        },
      },
      {
        "@type": "Question",
        name: "Qual a diferença entre BANT e MEDDIC?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "BANT é um framework de qualificação básica (Budget, Authority, Need, Timing) ideal para vendas simples. MEDDIC é um framework enterprise (Metrics, Economic Buyer, Decision Criteria, Decision Process, Identify Pain, Champion) para vendas complexas de alto valor com múltiplos decisores.",
        },
      },
      {
        "@type": "Question",
        name: "O que é SPIN Selling e como funciona?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "SPIN Selling é um método de venda consultiva criado por Neil Rackham baseado em quatro tipos de perguntas: Situation (situação), Problem (problema), Implication (implicação) e Need-payoff (necessidade-benefício). Ele guia o prospect a perceber o valor da solução por conta própria.",
        },
      },
    ],
  };

  const getLevelBadge = (levelNum: number) => {
    const colors: Record<number, string> = {
      1: "bg-accent/20 text-accent border-accent/40",
      2: "bg-primary/20 text-primary border-primary/40",
      3: "bg-secondary/20 text-secondary border-secondary/40",
      4: "bg-primary/20 text-primary border-primary/40",
    };
    const labels: Record<number, string> = {
      1: "Nível 1",
      2: "Nível 2",
      3: "Nível 3",
      4: "Nível 4",
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${colors[levelNum]}`}>
        {labels[levelNum]}
      </span>
    );
  };

  const getComplexityColor = (complexity: string) => {
    switch (complexity) {
      case "Baixa": return "text-accent";
      case "Média": return "text-primary";
      case "Alta": return "text-secondary";
      case "Muito Alta": return "text-primary font-bold";
      default: return "text-muted-foreground";
    }
  };

  return (
    <>
      <SEOHead
        title="13 Estratégias de Vendas Para Dominar 2026 | Guia Definitivo com Tabela"
        description="SPIN, BANT, MEDDIC, CHAMP e mais 9 frameworks de vendas explicados com exemplos práticos, tabela comparativa e nível de maturidade. O guia mais completo de técnicas de vendas para 2026."
        keywords="estratégias de vendas 2026, técnicas de vendas, SPIN selling, BANT, MEDDIC, CHAMP, frameworks de vendas, qualificação de leads, metodologia de vendas, como vender mais"
        canonicalUrl="https://bianchimbusiness.com.br/posts/13-estrategias-vendas-2026"
        ogType="article"
        articlePublishedTime="2026-01-13T00:00:00Z"
        articleAuthor="Junior Bianchim"
      />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqData) }} />

      <div className="min-h-screen bg-gradient-to-br from-background via-background to-accent/10">
        <Header />

        <main id="main-content">
          <article>
            {/* Hero Section */}
            <header className="relative py-12 sm:py-20 md:py-32 overflow-hidden">
              <div
                className="absolute inset-0 bg-cover bg-center opacity-30"
                style={{ backgroundImage: `url(${postCoverEstrategias})` }}
                role="img"
                aria-label="Frameworks estratégicos de vendas"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-background/80 to-background" />

              <div className="container mx-auto px-4 relative z-10">
                <div className="max-w-4xl mx-auto text-center">
                  <div className="flex items-center justify-center gap-4 mb-6 flex-wrap">
                    <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary/50 bg-primary/10 backdrop-blur-sm">
                      <Target className="w-4 h-4 text-primary" aria-hidden="true" />
                      <span className="text-sm font-medium text-primary">Técnicas de Vendas</span>
                    </span>
                    <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-accent/50 bg-accent/10 backdrop-blur-sm">
                      <TrendingUp className="w-4 h-4 text-accent" aria-hidden="true" />
                      <span className="text-sm font-medium text-accent">2026</span>
                    </span>
                  </div>

                  <h1 className="text-3xl sm:text-4xl md:text-6xl font-bold leading-tight mb-6">
                    <span className="bg-gradient-to-r from-primary via-accent to-secondary bg-clip-text text-transparent">
                      13 Estratégias de Vendas
                    </span>
                    <br />
                    <span className="text-foreground">Para Dominar Qualquer Negociação em 2026</span>
                  </h1>

                  <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
                    O guia definitivo com frameworks de BANT a MEDDIC. Tabela comparativa, níveis de maturidade e aplicação prática para cada cenário.
                  </p>

                  <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-primary" aria-hidden="true" />
                      <span>Por <strong className="text-foreground">Junior Bianchim</strong></span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-primary" aria-hidden="true" />
                      <time dateTime="2026-01-13">13 Jan 2026</time>
                      <span>• 25 min de leitura</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <BookOpen className="w-4 h-4 text-primary" aria-hidden="true" />
                      <span>+3.000 palavras</span>
                    </div>
                  </div>
                </div>
              </div>
            </header>

            {/* Intro */}
            <section className="py-10 sm:py-16 bg-muted/30" aria-labelledby="intro-heading">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto">
                  <h2 id="intro-heading" className="text-2xl sm:text-3xl font-bold mb-6">
                    Por Que Dominar Múltiplos Frameworks é o Diferencial dos Top Performers
                  </h2>

                  <div className="prose prose-lg max-w-none text-muted-foreground space-y-4">
                    <p>
                      Em 2026, o mercado de vendas B2B está mais competitivo e sofisticado do que nunca. Compradores têm acesso ilimitado a informação, comparam fornecedores com um clique e esperam vendedores que entendam profundamente seus desafios de negócio — não que recitem features de produto.
                    </p>
                    <p>
                      Nesse cenário, ter <strong className="text-foreground">apenas uma abordagem de vendas é como ter apenas um martelo</strong>: tudo vira prego. Os vendedores de alta performance dominam múltiplos frameworks e sabem exatamente quando usar cada um, adaptando sua estratégia ao contexto do cliente, ao ticket do deal e à complexidade da venda.
                    </p>
                    <p>
                      Neste artigo, vamos mergulhar em <strong className="text-foreground">13 das mais poderosas estratégias de vendas do mundo</strong> — desde o clássico BANT até o sofisticado MEDDIC. Para cada uma, você vai entender a estrutura, quando usar, e ver exemplos práticos de aplicação. No final, uma tabela comparativa e um guia de maturidade para montar seu arsenal estratégico ideal.
                    </p>
                  </div>

                  <div className="grid sm:grid-cols-3 gap-4 mt-8">
                    <Card className="p-4 bg-card border-primary/20 text-center">
                      <Target className="w-8 h-8 text-primary mx-auto mb-2" />
                      <p className="text-2xl font-bold text-foreground">13</p>
                      <p className="text-sm text-muted-foreground">Frameworks detalhados</p>
                    </Card>
                    <Card className="p-4 bg-card border-accent/20 text-center">
                      <Layers className="w-8 h-8 text-accent mx-auto mb-2" />
                      <p className="text-2xl font-bold text-foreground">4</p>
                      <p className="text-sm text-muted-foreground">Níveis de maturidade</p>
                    </Card>
                    <Card className="p-4 bg-card border-secondary/20 text-center">
                      <BarChart3 className="w-8 h-8 text-secondary mx-auto mb-2" />
                      <p className="text-2xl font-bold text-foreground">Tabela</p>
                      <p className="text-sm text-muted-foreground">Comparativa completa</p>
                    </Card>
                  </div>
                </div>
              </div>
            </section>

            {/* Strategies */}
            <section className="py-10 sm:py-16" aria-labelledby="strategies-heading">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto">
                  <h2 id="strategies-heading" className="text-2xl sm:text-3xl font-bold mb-10 text-center">
                    As 13 Estratégias Que Todo Vendedor Precisa Conhecer
                  </h2>

                  <div className="space-y-12">
                    {strategies.map((strategy) => {
                      const Icon = strategy.icon;
                      return (
                        <Card key={strategy.number} className="p-6 sm:p-8 border-border bg-card" id={`strategy-${strategy.number}`}>
                          <div className="flex items-start gap-4 mb-6">
                            <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center">
                              <Icon className={`w-7 h-7 ${strategy.color}`} />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2 flex-wrap">
                                <span className="text-sm font-mono text-muted-foreground">#{strategy.number}</span>
                                {getLevelBadge(strategy.levelNum)}
                              </div>
                              <h3 className="text-xl sm:text-2xl font-bold text-foreground">{strategy.name}</h3>
                              <p className="text-sm text-muted-foreground mt-1">
                                {strategy.author} • {strategy.level}
                              </p>
                            </div>
                          </div>

                          {/* Structure */}
                          <div className="mb-6">
                            <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">Estrutura</h4>
                            <div className="flex flex-wrap gap-2">
                              {strategy.structure.map((item) => (
                                <span key={item} className="px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium border border-primary/20">
                                  {item}
                                </span>
                              ))}
                            </div>
                          </div>

                          {/* Objective & When to Use */}
                          <div className="grid sm:grid-cols-2 gap-4 mb-6">
                            <div className="p-4 rounded-lg bg-muted/30 border border-border">
                              <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
                                <Lightbulb className="w-4 h-4 text-accent" />
                                Objetivo
                              </h4>
                              <p className="text-sm text-muted-foreground">{strategy.objective}</p>
                            </div>
                            <div className="p-4 rounded-lg bg-muted/30 border border-border">
                              <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
                                <CheckCircle className="w-4 h-4 text-accent" />
                                Quando usar
                              </h4>
                              <ul className="space-y-1">
                                {strategy.whenToUse.map((use) => (
                                  <li key={use} className="text-sm text-muted-foreground flex items-center gap-2">
                                    <ChevronRight className="w-3 h-3 text-primary flex-shrink-0" />
                                    {use}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>

                          {/* Description */}
                          <div className="prose prose-sm max-w-none text-muted-foreground">
                            {strategy.description.split("\n\n").map((paragraph, i) => (
                              <p key={i} dangerouslySetInnerHTML={{
                                __html: paragraph
                                  .replace(/\*\*(.*?)\*\*/g, '<strong class="text-foreground">$1</strong>')
                              }} />
                            ))}
                          </div>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              </div>
            </section>

            {/* Comparison Table */}
            <section className="py-10 sm:py-16 bg-muted/30" aria-labelledby="comparison-heading">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto">
                  <h2 id="comparison-heading" className="text-2xl sm:text-3xl font-bold mb-8 text-center">
                    Tabela Comparativa Estratégica
                  </h2>

                  <Card className="overflow-hidden border-border">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-muted/50">
                          <TableHead className="font-bold text-foreground">Modelo</TableHead>
                          <TableHead className="font-bold text-foreground">Foco Principal</TableHead>
                          <TableHead className="font-bold text-foreground">Complexidade</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {comparisonTable.map((row) => (
                          <TableRow key={row.model}>
                            <TableCell className="font-semibold text-foreground">{row.model}</TableCell>
                            <TableCell className="text-muted-foreground">{row.focus}</TableCell>
                            <TableCell className={getComplexityColor(row.complexity)}>{row.complexity}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Card>
                </div>
              </div>
            </section>

            {/* Maturity Levels */}
            <section className="py-10 sm:py-16" aria-labelledby="maturity-heading">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto">
                  <h2 id="maturity-heading" className="text-2xl sm:text-3xl font-bold mb-8 text-center">
                    Leitura Estratégica: Níveis de Maturidade
                  </h2>

                  <div className="space-y-6">
                    {maturityLevels.map((level) => {
                      const Icon = level.icon;
                      return (
                        <Card key={level.level} className={`p-6 border ${level.color}`}>
                          <div className="flex items-start gap-4">
                            <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-card flex items-center justify-center border border-border">
                              <Icon className="w-6 h-6 text-primary" />
                            </div>
                            <div className="flex-1">
                              <h3 className="text-lg font-bold text-foreground mb-2">{level.level}</h3>
                              <p className="text-sm text-muted-foreground mb-3">{level.description}</p>
                              <div className="flex flex-wrap gap-2">
                                {level.models.map((model) => (
                                  <span key={model} className="px-3 py-1 rounded-full bg-card text-foreground text-sm font-medium border border-border">
                                    {model}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              </div>
            </section>

            {/* Conclusion */}
            <section className="py-10 sm:py-16 bg-muted/30" aria-labelledby="conclusion-heading">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto">
                  <h2 id="conclusion-heading" className="text-2xl sm:text-3xl font-bold mb-6">
                    Como Escolher o Framework Certo Para Você
                  </h2>

                  <div className="prose prose-lg max-w-none text-muted-foreground space-y-4">
                    <p>
                      Não existe framework perfeito — existe o framework certo para o <strong className="text-foreground">momento certo</strong>. O segredo dos vendedores de alta performance é dominar pelo menos 2-3 frameworks complementares e alternar entre eles conforme o contexto exige.
                    </p>
                    <p>
                      <strong className="text-foreground">Se você está começando</strong>, domine BANT para qualificação rápida e SPIN para discovery. Esses dois sozinhos já cobrem 80% dos cenários que você vai encontrar.
                    </p>
                    <p>
                      <strong className="text-foreground">Se você vende SaaS B2B</strong>, adicione CHAMP ou NEAT ao seu arsenal. Eles são ágeis, modernos e ressoam com compradores de tecnologia.
                    </p>
                    <p>
                      <strong className="text-foreground">Se você vende enterprise</strong>, MEDDIC é inegociável. Combine com PICC para cobrir o aspecto político e DECIDE para mapear o processo decisório completo.
                    </p>
                    <p>
                      Independentemente do framework escolhido, lembre-se: <strong className="text-foreground">frameworks são guias, não prisões</strong>. A melhor aplicação de qualquer metodologia é aquela que parece uma conversa natural, não um interrogatório. Pratique até internalizar, e depois esqueça a sigla — foque no prospect.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Author */}
            <section className="py-10 sm:py-16">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto">
                  <Card className="p-6 sm:p-8 border-primary/20 bg-gradient-to-br from-card to-primary/5">
                    <div className="flex flex-col sm:flex-row items-center gap-6">
                      <div className="w-24 h-24 rounded-full overflow-hidden flex-shrink-0 ring-2 ring-primary/30">
                        <img src={juniorBianchim} alt="Junior Bianchim" className="w-full h-full object-cover" />
                      </div>
                      <div className="text-center sm:text-left">
                        <h3 className="text-xl font-bold text-foreground mb-1">Junior Bianchim</h3>
                        <p className="text-primary font-medium text-sm mb-3">Especialista em Treinamento de Equipes Comerciais</p>
                        <p className="text-muted-foreground text-sm">
                          Com mais de 20 anos de experiência em vendas B2B, Junior já treinou centenas de equipes comerciais
                          usando frameworks como SPIN, MEDDIC e GPCTBA. Fundador do Vendas Trainer, plataforma de IA para treinamento de vendedores.
                        </p>
                      </div>
                    </div>
                  </Card>
                </div>
              </div>
            </section>

            {/* CTA */}
            <section className="py-16 sm:py-20 bg-muted/30">
              <div className="container mx-auto px-4">
                <Card className="p-8 sm:p-12 bg-gradient-to-br from-card via-primary/5 to-accent/10 border-primary/30 text-center max-w-4xl mx-auto">
                  <div className="relative z-10">
                    <h2 className="text-3xl md:text-4xl font-bold mb-4">
                      <span className="text-gradient-brand">Pratique Esses Frameworks com IA</span>
                    </h2>
                    <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
                      No Vendas Trainer, você simula negociações reais usando cada um desses frameworks.
                      A IA avalia sua performance e dá feedback instantâneo.
                      <strong className="text-foreground"> Comece grátis.</strong>
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                      <Button
                        size="lg"
                        onClick={() => navigate("/auth?mode=signup")}
                        className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-glow text-lg px-8 py-6 font-semibold"
                      >
                        Criar Conta Grátis
                        <ArrowRight className="ml-2 w-5 h-5" />
                      </Button>
                      <Button size="lg" variant="outline" className="text-lg px-8 py-6 border-primary/50 hover:bg-primary/10" asChild>
                        <a href="https://wa.me/5516981960018" target="_blank" rel="noopener noreferrer">
                          Agendar Demonstração
                        </a>
                      </Button>
                    </div>
                  </div>
                </Card>
              </div>
            </section>
          </article>

          {/* Navigation */}
          <PageNavigation />
        </main>

        <Footer />
      </div>
    </>
  );
};

export default EstrategiasVendas2026;
